// ============================================
// MODALCOLECCIONORIGEN.JS — Motor de Origen Fase 2 (origen Coleccion)
// ============================================
// Navegador de 2 pasos para traer productos DESDE una coleccion al carrito de la
// cotizacion (el carrito ya fue SEMBRADO una vez por el padre con las lineas
// actuales). Paso A: elegir coleccion. Paso B: elegir VARIOS items (checkbox) y
// "Agregar N a la cotizacion".
//
// REGLA DE DINERO: NO vive aqui. Se usa el helper compartido
// construirAgregadoDesdeItemColeccion(item, prod) -> { tipo, referencia_id, extra }.
// Congelado vigente -> precio forzado + snapshot; vencido/no congelado -> precio de
// hoy + variante. Idéntico a la vista de Colecciones.
//
// Props:
//  - onTerminado(agregados:int): el padre hace volverACotizacion() para regresar.
//  - onCerrar(): cancela sin agregar nada (el carrito sembrado se queda; el padre
//    decide; por defecto al volver a la cotizacion las lineas originales siguen ahi).

import React, { useEffect, useMemo, useState } from 'react';
import { Layers, Lock, X, ArrowLeft, Search, Package, ShoppingCart, ChevronRight } from 'lucide-react';
import {
  PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE, COLOR_FONDO, API_BASE_CLI,
} from './helpersClientes';
import { tokens } from '../styles/tokens';
import { fetchAuth } from '../utils/sesion';
import { construirAgregadoDesdeItemColeccion } from '../utils/carritoOrigen';
import { useCarrito } from './CarritoFlotante';

const fmtMXN = (n) => (n == null || !Number.isFinite(Number(n)))
  ? '—'
  : Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Sello de estado de la coleccion (igual criterio que la vista de Colecciones).
function SelloEstado({ estado }) {
  if (!estado || estado === 'vigente') return null;
  const cfg = estado === 'vencido'
    ? { txt: 'Vencidos', bg: 'rgba(224,92,92,0.12)', color: '#c0392b', bd: 'rgba(224,92,92,0.4)' }
    : { txt: 'Por vencer', bg: 'rgba(214,150,40,0.14)', color: '#b9770e', bd: 'rgba(214,150,40,0.45)' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase',
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.bd}`, borderRadius: 6, padding: '2px 6px',
    }}>{cfg.txt}</span>
  );
}

// Construye el `prod` (referencia con variante) que espera el helper para items NO
// congelados variables. Para congelados el helper ignora prod (usa precio forzado).
function prodParaHelper(item) {
  const p = item.producto || {};
  const baseRef = String(p.referencia_id || item.referencia_id || '').split(':')[0];
  if (p.tiene_variantes === true && p.variante_seleccionada_id != null) {
    return { referencia_id: `${baseRef}:${p.variante_seleccionada_id}` };
  }
  return { referencia_id: baseRef };
}

export default function ModalColeccionOrigen({ onTerminado, onCerrar }) {
  const { agregarItem } = useCarrito();

  const [paso, setPaso] = useState('A'); // 'A' elegir coleccion | 'B' elegir items
  const [colecciones, setColecciones] = useState([]);
  const [cargandoCols, setCargandoCols] = useState(true);
  const [errorCols, setErrorCols] = useState(null);

  const [colSel, setColSel] = useState(null); // coleccion elegida
  const [items, setItems] = useState([]);
  const [cargandoItems, setCargandoItems] = useState(false);
  const [errorItems, setErrorItems] = useState(null);

  const [seleccion, setSeleccion] = useState({}); // { [itemId]: cantidad }
  const [busqueda, setBusqueda] = useState('');
  const [agregando, setAgregando] = useState(false);
  const [aviso, setAviso] = useState(null);

  // Paso A: cargar colecciones activas.
  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const resp = await fetchAuth(`${API_BASE_CLI}/api/colecciones`);
        const d = await resp.json().catch(() => ({}));
        if (!activo) return;
        if (d && d.ok) setColecciones((d.colecciones || []).filter(c => c.activa !== false));
        else setErrorCols((d && d.error) || 'No se pudieron cargar las colecciones');
      } catch (e) {
        if (activo && e.code !== 'SESION_EXPIRADA') setErrorCols('Sin conexion');
      } finally {
        if (activo) setCargandoCols(false);
      }
    })();
    return () => { activo = false; };
  }, []);

  const abrirColeccion = async (col) => {
    setColSel(col);
    setPaso('B');
    setItems([]);
    setSeleccion({});
    setBusqueda('');
    setErrorItems(null);
    setCargandoItems(true);
    try {
      const resp = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${col.id}`);
      const d = await resp.json().catch(() => ({}));
      if (d && d.ok) setItems(d.items || []);
      else setErrorItems((d && d.error) || 'No se pudieron cargar los productos');
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setErrorItems('Sin conexion');
    } finally {
      setCargandoItems(false);
    }
  };

  const volverPasoA = () => { setPaso('A'); setColSel(null); setItems([]); setSeleccion({}); setAviso(null); };

  const toggleItem = (it) => {
    setSeleccion(prev => {
      const next = { ...prev };
      if (next[it.id] != null) delete next[it.id];
      else next[it.id] = 1;
      return next;
    });
  };
  const fijarCantidad = (id, val) => {
    const n = Math.max(1, Math.round(Number(val) || 1));
    setSeleccion(prev => (prev[id] != null ? { ...prev, [id]: n } : prev));
  };

  const itemsFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => {
      const nom = (it.producto?.nombre || it.nombre || '').toLowerCase();
      const mod = (it.producto?.modelo || it.modelo || '').toLowerCase();
      return nom.includes(q) || mod.includes(q);
    });
  }, [items, busqueda]);

  const totalSel = Object.keys(seleccion).length;

  // Confirmar: por cada item seleccionado, helper -> agregarItem. Si alguno falla,
  // se continua con el resto y se avisa al final (no rompe la operacion).
  const confirmar = async () => {
    if (totalSel === 0 || agregando) return;
    setAgregando(true);
    setAviso(null);
    let ok = 0;
    const fallos = [];
    for (const it of items) {
      const cant = seleccion[it.id];
      if (cant == null) continue;
      try {
        const { tipo, referencia_id, extra } = construirAgregadoDesdeItemColeccion(it, prodParaHelper(it));
        await agregarItem({ tipo, referencia_id }, cant, extra);
        ok++;
      } catch (e) {
        fallos.push(it.producto?.nombre || it.nombre || `item ${it.id}`);
      }
    }
    setAgregando(false);
    if (fallos.length) {
      setAviso(`Se agregaron ${ok}. No se pudieron agregar: ${fallos.join(', ')}.`);
      return; // dejar el modal abierto para reintentar; no cerramos en error parcial
    }
    onTerminado && onTerminado(ok);
  };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1250,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Traer productos desde una coleccion"
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560,
          fontFamily: PP, color: COLOR_GRIS, display: 'flex', flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* Cabecera */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 20px', borderBottom: `1px solid ${COLOR_BORDE}` }}>
          {paso === 'B' && (
            <button type="button" onClick={volverPasoA} aria-label="Volver"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: COLOR_GRIS, padding: 2, display: 'inline-flex' }}>
              <ArrowLeft size={20} strokeWidth={2} />
            </button>
          )}
          <span style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 8, background: tokens.primarySoft, color: tokens.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Layers size={18} strokeWidth={2} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OSCURO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {paso === 'A' ? 'Elige una coleccion' : (colSel?.nombre || 'Coleccion')}
            </div>
            <div style={{ fontSize: 12, color: COLOR_GRIS }}>
              {paso === 'A' ? 'De donde traer los productos' : 'Marca los productos a agregar'}
            </div>
          </div>
          <button type="button" onClick={onCerrar} aria-label="Cerrar"
            style={{ flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer', color: COLOR_GRIS_CLARO, padding: 4, display: 'inline-flex' }}>
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        {/* Cuerpo */}
        <div style={{ overflowY: 'auto', padding: paso === 'A' ? 12 : 0, flex: 1 }}>
          {paso === 'A' && (
            <>
              {cargandoCols && <div style={{ padding: 24, textAlign: 'center', color: COLOR_GRIS_CLARO }}>Cargando colecciones…</div>}
              {errorCols && <div style={{ padding: 16, color: tokens.danger, fontSize: 13 }}>{errorCols}</div>}
              {!cargandoCols && !errorCols && colecciones.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: COLOR_GRIS, fontSize: 13 }}>No hay colecciones activas.</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {colecciones.map(col => (
                  <button key={col.id} type="button" onClick={() => abrirColeccion(col)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                      padding: '12px 14px', borderRadius: 10, border: `1px solid ${COLOR_BORDE}`, background: '#fff',
                      cursor: 'pointer', fontFamily: PP,
                    }}>
                    <span style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, background: COLOR_FONDO, color: tokens.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {col.congelada ? <Lock size={16} strokeWidth={2} /> : <Layers size={16} strokeWidth={2} />}
                    </span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.nombre}</span>
                        <SelloEstado estado={col.estado_lista} />
                      </span>
                      <span style={{ display: 'block', fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 1 }}>
                        {Number(col.items_total) || 0} producto{Number(col.items_total) === 1 ? '' : 's'}
                        {col.congelada ? ' · precios congelados' : ''}
                      </span>
                    </span>
                    <ChevronRight size={18} strokeWidth={2} color={COLOR_GRIS_CLARO} style={{ flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            </>
          )}

          {paso === 'B' && (
            <>
              {/* Buscador */}
              <div style={{ position: 'sticky', top: 0, background: '#fff', padding: '12px 16px', borderBottom: `1px solid ${COLOR_BORDE}`, zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${COLOR_BORDE}`, borderRadius: 9, padding: '8px 10px', background: COLOR_FONDO }}>
                  <Search size={16} strokeWidth={2} color={COLOR_GRIS_CLARO} />
                  <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por nombre o modelo…"
                    style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, fontFamily: PP, color: COLOR_OSCURO }} />
                </div>
              </div>

              {cargandoItems && <div style={{ padding: 24, textAlign: 'center', color: COLOR_GRIS_CLARO }}>Cargando productos…</div>}
              {errorItems && <div style={{ padding: 16, color: tokens.danger, fontSize: 13 }}>{errorItems}</div>}
              {!cargandoItems && !errorItems && itemsFiltrados.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: COLOR_GRIS, fontSize: 13 }}>
                  {items.length === 0 ? 'Esta coleccion no tiene productos.' : 'Sin resultados para tu busqueda.'}
                </div>
              )}

              <div>
                {itemsFiltrados.map(it => {
                  const p = it.producto || {};
                  const sel = seleccion[it.id] != null;
                  const congeladoVig = it.congelado === true && it.congelado_estado !== 'vencido' && it.precio_congelado_mxn != null;
                  const precioMostrar = p.precio_efectivo_mxn != null ? p.precio_efectivo_mxn : it.precio_congelado_mxn;
                  return (
                    <label key={it.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                      borderBottom: `1px solid ${COLOR_BORDE}`, cursor: 'pointer',
                      background: sel ? tokens.primarySoft : '#fff',
                    }}>
                      <input type="checkbox" checked={sel} onChange={() => toggleItem(it)}
                        style={{ width: 18, height: 18, flexShrink: 0, accentColor: tokens.primary, cursor: 'pointer' }} />
                      <span style={{ flexShrink: 0, width: 48, height: 48, borderRadius: 8, background: COLOR_FONDO, border: `1px solid ${COLOR_BORDE}`, overflow: 'hidden', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        {(p.imagen_url || it.imagen_url)
                          ? <img src={p.imagen_url || it.imagen_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          : <Package size={20} strokeWidth={1.5} color={COLOR_GRIS_CLARO} />}
                      </span>
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLOR_OSCURO, lineHeight: 1.3 }}>{p.nombre || it.nombre}</span>
                        {(p.modelo) && <span style={{ display: 'block', fontSize: 12, color: '#6d28d9', fontWeight: 800, fontFamily: 'monospace', marginTop: 1 }}>{p.modelo}</span>}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>{fmtMXN(precioMostrar)}</span>
                          {congeladoVig && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 800, color: '#2f5f9e', background: 'rgba(47,95,158,0.1)', border: '1px solid rgba(47,95,158,0.3)', borderRadius: 5, padding: '1px 5px' }}>
                              <Lock size={9} strokeWidth={2.5} /> Precio congelado
                            </span>
                          )}
                        </span>
                      </span>
                      {sel && (
                        <input type="number" min="1" step="1" value={seleccion[it.id]} onClick={(e) => e.preventDefault()}
                          onChange={(e) => fijarCantidad(it.id, e.target.value)}
                          style={{ flexShrink: 0, width: 56, textAlign: 'center', padding: '6px 4px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, fontSize: 13, fontFamily: PP, color: COLOR_OSCURO }} />
                      )}
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Pie: solo en paso B, boton Agregar N */}
        {paso === 'B' && (
          <div style={{ padding: '14px 16px', borderTop: `1px solid ${COLOR_BORDE}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {aviso && <div style={{ fontSize: 12, color: tokens.warning, fontWeight: 600 }}>{aviso}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onCerrar} disabled={agregando}
                style={{ flexShrink: 0, padding: '11px 16px', borderRadius: 9, border: `1px solid ${COLOR_BORDE}`, background: '#fff', color: COLOR_GRIS, fontSize: 13, fontWeight: 700, fontFamily: PP, cursor: agregando ? 'default' : 'pointer' }}>
                Cancelar
              </button>
              <button type="button" onClick={confirmar} disabled={totalSel === 0 || agregando}
                style={{
                  flex: 1, padding: '11px 16px', borderRadius: 9, border: 'none',
                  background: (totalSel === 0 || agregando) ? COLOR_GRIS_CLARO : tokens.primary,
                  color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: PP,
                  cursor: (totalSel === 0 || agregando) ? 'default' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                <ShoppingCart size={16} strokeWidth={2} />
                {agregando ? 'Agregando…' : (totalSel === 0 ? 'Selecciona productos' : `Agregar ${totalSel} a la cotizacion`)}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
